import React from "react";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getPrimaryNavItems,
  getQuickActionItems,
  getSecondaryNavItems,
  isNavItemActive,
} from "./navigation/nav-config.jsx";
import { trackNavigationIntent } from "../lib/app-telemetry";
import { useOrganization } from "../contexts/OrganizationContext";

const DesktopSidebar = ({ session }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasFeature } = useOrganization() || {};
  const hasStaffFeature = hasFeature ? hasFeature('staff_compensation') : true;
  const hasMemberships = hasFeature ? hasFeature('memberships') : true;
  const primaryItems = getPrimaryNavItems(session);
  const secondaryItems = getSecondaryNavItems(session, hasStaffFeature, hasMemberships);
  const quickActions = getQuickActionItems(session, hasMemberships);

  const handleNavigate = (item, source, trigger) => {
    trackNavigationIntent({
      source,
      trigger,
      from: location.pathname,
      to: item.path,
      label: item.label,
    });
    navigate(item.path);
  };

  const renderNavButton = (item, source) => {
    const Icon = item.icon;
    const active = isNavItemActive(location.pathname, item);

    return (
      <Button
        key={item.path}
        onClick={() => handleNavigate(item, source, "desktop_sidebar")}
        startIcon={<Icon />}
        variant={active ? "contained" : "text"}
        fullWidth
        sx={{
          justifyContent: "flex-start",
          px: 1.5,
          py: 1,
          borderRadius: 3,
          color: active ? "primary.contrastText" : "text.secondary",
          bgcolor: active ? "primary.main" : "transparent",
          fontWeight: active ? 600 : 500,
          "&:hover": {
            bgcolor: active ? "primary.main" : "rgba(148, 163, 184, 0.08)",
          },
        }}
      >
        {item.label}
      </Button>
    );
  };

  return (
    <Box
      component="aside"
      sx={{
        width: 224,
        flexShrink: 0,
        alignSelf: "stretch",
        position: "sticky",
        top: 0,
        height: "100vh",
      }}
    >
      <Box
        sx={{
          height: "100%",
          overflowY: "auto",
          px: 1.5,
          pt: 1.5,
          pb: 2.5,
          borderRight: "1px solid",
          borderColor: "divider",
        }}
      >
        <Stack spacing={1}>
          {primaryItems.map((item) =>
            renderNavButton(item, "desktop_sidebar_primary"),
          )}
        </Stack>

        {secondaryItems.length > 0 ? (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Typography
              variant="overline"
              sx={{ color: "text.secondary", letterSpacing: "0.08em" }}
            >
              Accesos
            </Typography>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {secondaryItems.map((item) =>
                renderNavButton(item, "desktop_sidebar_secondary"),
              )}
            </Stack>
          </>
        ) : null}

        {quickActions.length > 0 ? (
          <>
            <Divider sx={{ my: 2.5 }} />
            <Typography
              variant="overline"
              sx={{ color: "text.secondary", letterSpacing: "0.08em" }}
            >
              Acciones Rápidas
            </Typography>
            <Stack spacing={1.25} sx={{ mt: 1.25 }}>
              {quickActions.map((item) => {
                const Icon = item.icon;

                return (
                  <Button
                    key={item.path}
                    onClick={() =>
                      handleNavigate(
                        item,
                        "desktop_sidebar_quick_actions",
                        "desktop_sidebar_action",
                      )
                    }
                    startIcon={<Icon />}
                    variant="outlined"
                    fullWidth
                    sx={{
                      justifyContent: "flex-start",
                      px: 1.5,
                      py: 1,
                      borderRadius: 3,
                      borderColor: `${item.color}55`,
                      color: "text.primary",
                      "&:hover": {
                        borderColor: item.color,
                        bgcolor: `${item.color}14`,
                      },
                    }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </Stack>
          </>
        ) : null}
      </Box>
    </Box>
  );
};

export default DesktopSidebar;
